import { Injectable, inject } from '@angular/core';
import {
    AppStatus,
    Document,
    DocumentRequirementType,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { Observable, combineLatest, filter, map } from 'rxjs';
import { DocumentChecklistItem } from '../../../document-checklist/document-checklist.model';
import { DocumentsFacadeService } from '../documents-facade';

@Injectable({
    providedIn: 'root',
})
export class DocumentRequirementsService {
    activeDocumentChecklistItems: DocumentChecklistItem[];

    private documentsFacadeService = inject(DocumentsFacadeService);

    getItems$(
        entityId: string,
        entityType: SystemEntity,
        documentRequirementCategories: string[],
        app: AppStatus,
        skipLoading = false,
    ): Observable<DocumentChecklistItem[]> {
        if (!skipLoading) {
            this.documentsFacadeService.loadDocumentRequirements(
                entityId,
                entityType,
                documentRequirementCategories,
            );
        }
        return combineLatest([
            this.documentsFacadeService.getDocumentRequirements$(
                entityId,
                entityType,
            ),
            this.documentsFacadeService.getDocumentRequirementTypes$(app),
        ]).pipe(
            filter(([documentRequirements, documentRequirementTypes]) => {
                return (
                    Boolean(documentRequirementTypes) &&
                    Boolean(documentRequirements)
                );
            }),
            map(([documentRequirements, documentRequirementTypes]) => {
                return documentRequirements.map((documentRequirement) => {
                    return {
                        documentRequirement,
                        documentRequirementType:
                            this.getDocumentRequirementType(
                                documentRequirement.requirementType.code,
                                documentRequirementTypes,
                            ),
                    };
                });
            }),
        );
    }

    getAllDocuments$(
        entityId: string,
        entityType: SystemEntity,
        documentRequirementCategories: string[],
        app: AppStatus,
        skipLoading = false,
    ): Observable<(Document & { documentRequirementId: string })[]> {
        return this.getItems$(
            entityId,
            entityType,
            documentRequirementCategories,
            app,
            skipLoading,
        ).pipe(
            map((documentRequirements) =>
                documentRequirements.flatMap(({ documentRequirement }) =>
                    documentRequirement.associatedDocuments.map((doc) => ({
                        ...doc,
                        documentRequirementId: documentRequirement.id,
                    })),
                ),
            ),
        );
    }

    getDocumentRequirementType(
        documentRequirementCode: string,
        documentRequirementTypes: DocumentRequirementType[],
    ): DocumentRequirementType {
        return documentRequirementTypes.find(
            (documentRequirementType) =>
                documentRequirementType?.code?.toLowerCase() ===
                documentRequirementCode.toLowerCase(),
        );
    }

    getExpiredDays(dateExpires: string): boolean {
        if (!dateExpires) {
            return false;
        }

        const currentDate = new Date();
        const expireDate = new Date(dateExpires);
        const diffInMs = expireDate.getTime() - currentDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays <= 30 && diffInDays > 0;
    }

    getIsExpired(dateExpires: string): boolean {
        if (!dateExpires) {
            return false;
        }

        const currentDate = new Date();
        const expireDate = new Date(dateExpires);
        return expireDate.getTime() < currentDate.getTime();
    }
}
